import { format, isBefore, startOfDay } from 'date-fns';
import { useState } from 'react';
import { T, DateTime, Var, useGT } from 'gt-react';
import { parseDateOnly } from '@/utils/dateUtils';
import { SystemEntityInline } from '@/components/system-entities/inline-ui';
import { SystemEntity } from '@erp/shared-types';
import { Button } from '@/components/ui/button';
import {
  Table,
  TableBody,
  TableCell,
  TableFooter,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { cn } from '@/lib/utils';
import type { Payment } from '@erp/rpc';
import { Badge } from '@/components/ui/badge';
import { DatePicker } from '@/components/common/date-pickers/date-picker';
import {
  SpinnerIcon,
  DotsThreeVerticalIcon,
  CalendarIcon,
  TrashIcon,
} from '@phosphor-icons/react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  formatCurrency,
  getInvoicePaymentStatusVariant,
} from './payment-formatting';

type PaymentInvoice = NonNullable<Payment['invoices']>[number];

interface InvoicesSectionProps {
  invoices: PaymentInvoice[];
  payment: Payment;
  showActions: boolean;
  removePending: boolean;
  reschedulePending: boolean;
  onRemove: (invoiceId: string) => void;
  onReschedule: (invoiceId: string, newDate: string) => void;
}

export function InvoicesSection({
  invoices,
  payment,
  showActions,
  removePending,
  reschedulePending,
  onRemove,
  onReschedule,
}: InvoicesSectionProps) {
  const [rescheduleInvoiceId, setRescheduleInvoiceId] = useState<string | null>(
    null,
  );
  const [rescheduleDate, setRescheduleDate] = useState<Date | undefined>(
    undefined,
  );
  const gt = useGT();
  const currency = payment.currency || 'USD';
  const paymentAmount = Number(payment.amount);
  const totalAllocated = invoices.reduce(
    (sum, inv) => sum + Number(inv.amount),
    0,
  );
  // Positive = payment exceeds allocations (over-paid).
  // Negative = payment falls short of allocations (under-paid).
  const variance = paymentAmount - totalAllocated;
  const absVariance = Math.abs(variance);
  // Tolerance for floating-point sum of two-decimal strings.
  const isMatch = absVariance < 0.005;
  const varianceTone = isMatch
    ? 'text-emerald-700 dark:text-emerald-400'
    : variance < 0
      ? 'text-amber-700 dark:text-amber-400'
      : 'text-destructive';
  const varianceLabel = isMatch
    ? gt('Payment matches allocations')
    : variance < 0
      ? gt('Under-paid by {amount}', {
          amount: formatCurrency(absVariance, currency),
        })
      : gt('Over-paid by {amount}', {
          amount: formatCurrency(absVariance, currency),
        });

  const paymentDate = parseDateOnly(payment.paymentDate);
  const todayStart = startOfDay(new Date());
  // PaymentExecutionService.syncInvoicePaymentStatus runs inside executeIdempotent
  // (status flips to `processing` at entry, `paid`/`failed` at exit), so allocations
  // are only applied to invoice.paidAmount once the payment has reached one of those
  // states. `approved` and `scheduled` still predate execution and must not subtract.
  const paidAmountIncludesAllocation =
    payment.status === 'processing' ||
    payment.status === 'paid' ||
    payment.status === 'failed' ||
    payment.status === 'canceled';

  return (
    <div className="space-y-3">
      <div className="flex items-baseline justify-between">
        <h3 className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
          {gt('Invoices ({count})', { count: String(invoices.length) })}
        </h3>
        <span className={cn('text-xs font-medium', varianceTone)}>
          {varianceLabel}
        </span>
      </div>

      <div className="rounded-md border overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow className="bg-muted/40 hover:bg-muted/40">
              <TableHead className="h-9 text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
                <T context="table column header for invoice">Invoice</T>
              </TableHead>
              <TableHead className="h-9 text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
                <T context="table column header for supplier">Supplier</T>
              </TableHead>
              <TableHead className="h-9 text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
                <T context="table column header for due date">Due</T>
              </TableHead>
              <TableHead className="h-9 text-right text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
                <T context="table column header for invoice total amount">
                  Invoice Total
                </T>
              </TableHead>
              <TableHead className="h-9 text-right text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
                <T context="table column header for amount allocated to this payment">
                  Allocated
                </T>
              </TableHead>
              <TableHead className="h-9 text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
                <T context="table column header for invoice payment status">
                  Status
                </T>
              </TableHead>
              {showActions && <TableHead className="h-9 w-10" />}
            </TableRow>
          </TableHeader>
          <TableBody>
            {invoices.map((inv) => {
              const invCurrency = inv.currency || currency;
              const allocated = Number(inv.amount);
              const invoiceTotal =
                inv.totalAmount !== undefined ? Number(inv.totalAmount) : null;
              const rawPaidAmount =
                inv.paidAmount !== null && inv.paidAmount !== undefined
                  ? Number(inv.paidAmount)
                  : 0;
              const priorPaid = paidAmountIncludesAllocation
                ? rawPaidAmount - allocated
                : rawPaidAmount;
              const remainingBefore =
                invoiceTotal !== null ? invoiceTotal - priorPaid : null;
              const remainingAfter =
                remainingBefore !== null ? remainingBefore - allocated : null;
              const allocatedClearsInvoice =
                remainingAfter !== null && Math.abs(remainingAfter) < 0.005;
              /* eslint-disable i18next/no-literal-string */
              const allocatedTone =
                remainingAfter === null
                  ? ''
                  : allocatedClearsInvoice
                    ? 'text-emerald-700 dark:text-emerald-400'
                    : remainingAfter > 0
                      ? 'text-foreground'
                      : 'text-destructive';
              /* eslint-enable i18next/no-literal-string */

              const dueDate = inv.dueDate ? parseDateOnly(inv.dueDate) : null;
              const isOverdue =
                dueDate !== null &&
                inv.paymentStatus !== 'paid' &&
                isBefore(dueDate, todayStart);
              // Flag invoices whose due date falls strictly before the payment
              // date — the invoice is due before this payment will be made.
              // Same-day is treated as on-time.
              const dueBeforePaymentDate =
                dueDate !== null &&
                paymentDate !== null &&
                isBefore(dueDate, paymentDate);

              return (
                <TableRow key={inv.id} className="group">
                  <TableCell className="py-2.5 align-middle">
                    <SystemEntityInline
                      entityType={SystemEntity.AP_INVOICE}
                      entityId={inv.id}
                    />
                  </TableCell>
                  <TableCell className="py-2.5 align-middle text-sm">
                    {inv.supplierId ? (
                      <SystemEntityInline
                        entityType={SystemEntity.AP_SUPPLIER}
                        entityId={inv.supplierId}
                      />
                    ) : (
                      <span className="text-muted-foreground">—</span>
                    )}
                  </TableCell>
                  <TableCell className="py-2.5 align-middle text-sm tabular-nums">
                    {dueDate ? (
                      <span
                        className={cn(
                          'inline-flex items-center gap-1.5',
                          isOverdue && 'text-amber-700 dark:text-amber-400',
                        )}
                      >
                        {isOverdue && (
                          <span
                            aria-hidden
                            className="size-1.5 rounded-full bg-amber-500"
                          />
                        )}
                        <T>
                          <DateTime
                            options={{
                              month: 'short',
                              day: 'numeric',
                              year: 'numeric',
                            }}
                          >
                            {dueDate}
                          </DateTime>
                        </T>
                        {!isOverdue && dueBeforePaymentDate && (
                          <span className="text-[10px] uppercase tracking-wide text-amber-600 dark:text-amber-500">
                            <T context="invoice table cell label, invoice due date falls before the payment date">
                              due before pay date
                            </T>
                          </span>
                        )}
                      </span>
                    ) : (
                      <span className="text-muted-foreground">—</span>
                    )}
                  </TableCell>
                  <TableCell className="py-2.5 align-middle text-right text-sm tabular-nums text-muted-foreground">
                    {invoiceTotal !== null
                      ? formatCurrency(invoiceTotal, invCurrency)
                      : '—'}
                  </TableCell>
                  <TableCell
                    className={cn(
                      'py-2.5 align-middle text-right text-sm font-medium tabular-nums',
                      allocatedTone,
                    )}
                  >
                    {remainingBefore !== null ? (
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <button
                            type="button"
                            className="cursor-default tabular-nums focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring rounded-sm"
                          >
                            {formatCurrency(allocated, invCurrency)}
                          </button>
                        </TooltipTrigger>
                        <TooltipContent side="top" className="text-xs">
                          <div className="space-y-0.5 tabular-nums">
                            <div>
                              <T context="invoice allocation tooltip label">
                                Invoice total:
                              </T>{' '}
                              {formatCurrency(invoiceTotal ?? 0, invCurrency)}
                            </div>
                            <div>
                              <T context="invoice allocation tooltip label">
                                Already paid:
                              </T>{' '}
                              {formatCurrency(priorPaid, invCurrency)}
                            </div>
                            <div>
                              <T context="invoice allocation tooltip label">
                                Remaining before:
                              </T>{' '}
                              {formatCurrency(remainingBefore, invCurrency)}
                            </div>
                            <div className="border-t pt-0.5 mt-0.5 font-medium">
                              <T context="invoice allocation tooltip label">
                                Remaining after:
                              </T>{' '}
                              {formatCurrency(remainingAfter ?? 0, invCurrency)}
                            </div>
                          </div>
                        </TooltipContent>
                      </Tooltip>
                    ) : (
                      formatCurrency(allocated, invCurrency)
                    )}
                  </TableCell>
                  <TableCell className="py-2.5 align-middle">
                    <Badge
                      variant={getInvoicePaymentStatusVariant(
                        inv.paymentStatus,
                      )}
                      className="text-[10px] uppercase tracking-wide"
                    >
                      {inv.paymentStatus ?? '—'}
                    </Badge>
                  </TableCell>
                  {showActions && (
                    <TableCell className="py-2.5 align-middle w-10 pr-2">
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button
                            variant="ghost"
                            size="sm"
                            aria-label={gt('Invoice actions')}
                            className="h-7 w-7 p-0 opacity-0 group-hover:opacity-100 focus-visible:opacity-100 transition-opacity"
                            disabled={removePending || reschedulePending}
                          >
                            <DotsThreeVerticalIcon className="h-4 w-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem
                            onSelect={() => {
                              setRescheduleInvoiceId(inv.id);
                              setRescheduleDate(undefined);
                            }}
                          >
                            <CalendarIcon className="mr-2 h-4 w-4" />
                            <T>Reschedule</T>
                          </DropdownMenuItem>
                          <DropdownMenuItem
                            className="text-destructive focus:text-destructive"
                            onSelect={() => onRemove(inv.id)}
                          >
                            <TrashIcon className="mr-2 h-4 w-4" />
                            <T>Remove</T>
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </TableCell>
                  )}
                </TableRow>
              );
            })}
          </TableBody>
          <TableFooter className="bg-muted/30">
            <TableRow className="hover:bg-transparent">
              <TableCell
                colSpan={4}
                className="py-2.5 text-xs uppercase tracking-wide text-muted-foreground"
              >
                <T context="table footer label for total allocated amount">
                  Total allocated
                </T>
              </TableCell>
              <TableCell className="py-2.5 text-right text-sm font-semibold tabular-nums">
                {formatCurrency(totalAllocated, currency)}
              </TableCell>
              <TableCell
                colSpan={showActions ? 2 : 1}
                className="py-2.5 text-xs text-muted-foreground"
              >
                <T context="table footer label showing payment amount">
                  Payment: <Var>{formatCurrency(paymentAmount, currency)}</Var>
                </T>
              </TableCell>
            </TableRow>
            {!isMatch && (
              <TableRow className="hover:bg-transparent border-t-0">
                <TableCell
                  colSpan={4}
                  className="py-2 text-xs uppercase tracking-wide text-muted-foreground"
                >
                  <T context="table footer label for payment vs allocation variance">
                    Variance
                  </T>
                </TableCell>
                <TableCell
                  className={cn(
                    'py-2 text-right text-sm font-semibold tabular-nums',
                    varianceTone,
                  )}
                >
                  {variance > 0 ? '+' : ''}
                  {formatCurrency(variance, currency)}
                </TableCell>
                <TableCell
                  colSpan={showActions ? 2 : 1}
                  className={cn('py-2 text-xs font-medium', varianceTone)}
                >
                  {varianceLabel}
                </TableCell>
              </TableRow>
            )}
          </TableFooter>
        </Table>
      </div>

      <Dialog
        open={rescheduleInvoiceId !== null}
        onOpenChange={(open) => {
          if (!open) {
            setRescheduleInvoiceId(null);
            setRescheduleDate(undefined);
          }
        }}
      >
        <DialogContent className="sm:max-w-[360px]">
          <DialogHeader>
            <DialogTitle>
              <T>Reschedule Invoice</T>
            </DialogTitle>
            <DialogDescription>
              <T>Move this invoice to a payment on a different date.</T>
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <DatePicker
              value={rescheduleDate}
              onChange={setRescheduleDate}
              placeholder={gt('Select new date', {
                context: 'date picker placeholder for rescheduling an invoice',
              })}
            />
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setRescheduleInvoiceId(null);
                setRescheduleDate(undefined);
              }}
            >
              <T>Cancel</T>
            </Button>
            <Button
              disabled={!rescheduleDate || reschedulePending}
              onClick={() => {
                if (rescheduleInvoiceId && rescheduleDate) {
                  onReschedule(
                    rescheduleInvoiceId,
                    format(rescheduleDate, 'yyyy-MM-dd'),
                  );
                  setRescheduleInvoiceId(null);
                  setRescheduleDate(undefined);
                }
              }}
            >
              {reschedulePending && (
                <SpinnerIcon className="mr-2 h-4 w-4 animate-spin" />
              )}
              <T>Reschedule</T>
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
